/** Leading-byte (magic number) signatures for the attachment MIME types we
 *  advertise. Text types have no signature and are never checked. */
const SIGNATURES: Record<string, number[][]> = {
  'application/pdf': [[0x25, 0x50, 0x44, 0x46]], // %PDF
  'image/jpeg': [[0xff, 0xd8, 0xff]],
  'image/png': [[0x89, 0x50, 0x4e, 0x47]],
  'image/gif': [[0x47, 0x49, 0x46, 0x38]], // GIF8
  // WebP is validated separately below: RIFF alone matches WAV/AVI too,
  // so the format tag at offset 8 must also read 'WEBP'.
  // OOXML (docx/xlsx) are zip archives.
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': [
    [0x50, 0x4b, 0x03, 0x04],
  ],
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': [
    [0x50, 0x4b, 0x03, 0x04],
  ],
  // Legacy Office are OLE compound files.
  'application/msword': [[0xd0, 0xcf, 0x11, 0xe0]],
  'application/vnd.ms-excel': [[0xd0, 0xcf, 0x11, 0xe0]],
};

/**
 * Whether `bytes` plausibly are the `declaredMime` the client claimed.
 *
 * Sniffs the leading bytes against the known signature for the declared type;
 * a mismatch means the upload is lying about its type (e.g. an executable
 * renamed to .pdf). Types without a known signature (text/*, anything not in
 * the table) return true — we can only verify what has a magic number.
 */
export function matchesFileSignature(
  declaredMime: string,
  bytes: ArrayBuffer
): boolean {
  if (declaredMime === 'image/webp') {
    const head = new Uint8Array(bytes.slice(0, 12));
    const riff = [0x52, 0x49, 0x46, 0x46].every((b, i) => head[i] === b);
    const webp = [0x57, 0x45, 0x42, 0x50].every((b, i) => head[i + 8] === b);
    return riff && webp;
  }

  const signatures = SIGNATURES[declaredMime];
  if (!signatures) return true;

  const head = new Uint8Array(bytes.slice(0, 8));
  return signatures.some(
    sig => sig.length <= head.length && sig.every((b, i) => head[i] === b)
  );
}
